import { fromJS, Map as IMap } from "immutable";
import {
  format,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfWeek,
  endOfWeek,
  startOfQuarter,
  endOfQuarter,
  subWeeks,
  subMonths,
  subYears,
  subQuarters,
  differenceInMilliseconds,
  subMilliseconds,
  parseISO,
} from "date-fns";

type ReportData = IMap<string, any>;

const toDateTime = (d: Date, end = false) =>
  format(end ? endOfDay(d) : startOfDay(d), "yyyy-MM-dd'T'HH:mm:ss");

const toSafeDate = (s: string) => {
  if (!s) return new Date(NaN);
  const normalized = s.includes("T") ? s : s.replace(" ", "T");
  return parseISO(normalized);
};

const calculatePreviousPeriod = (curStart: Date, curEnd: Date, comparisonBy: string) => {
  const duration = Math.max(0, differenceInMilliseconds(curEnd, curStart));
  switch (comparisonBy) {
    case "lastYear":
      return { prevStart: subYears(curStart, 1), prevEnd: subYears(curEnd, 1) };
    case "lastMonth":
      return {
        prevStart: subMonths(curStart, 1),
        prevEnd: subMonths(curEnd, 1),
      };
    case "lastWeek":
      return { prevStart: subWeeks(curStart, 1), prevEnd: subWeeks(curEnd, 1) };
    case "lastQuarter":
      // For quarters, compare current quarter vs previous quarter
      return {
        prevStart: subQuarters(curStart, 1),
        prevEnd: subQuarters(curEnd, 1),
      };
    default:
      return {
        prevStart: subMilliseconds(curStart, duration + 1000),
        prevEnd: subMilliseconds(curStart, 1000),
      };
  }
};

export const selectReportData = (state: any) => state.reportSetting.get("data");

export const buildComparisonFilters = (reportData: ReportData) => {
  const comparisonBy: string = reportData.get("comparisonBy") || "none";
  const baseFilters = reportData.get("filters") || fromJS({});
  const jsFilters =
    typeof (baseFilters as any)?.toJS === "function" ? baseFilters.toJS() : baseFilters;

  const hasExplicitRange =
    jsFilters &&
    jsFilters.start &&
    jsFilters.start.operator === "dateRange" &&
    Array.isArray(jsFilters.start.values) &&
    jsFilters.start.values.length >= 2;

  if (!hasExplicitRange && comparisonBy === "none") {
    return {
      currentFilters: fromJS(jsFilters || {}),
      previousFilters: fromJS({}),
      hasComparison: false,
    };
  }

  // When comparisonBy is set but no explicit range, create a default current range
  if (!hasExplicitRange && comparisonBy !== "none") {
    const now = new Date();
    let curStart = startOfDay(now);
    let curEnd = endOfDay(now);

    // Use comparisonBy to determine the current period, not dateUnit
    if (comparisonBy === "lastYear") {
      curStart = startOfYear(now);
      curEnd = endOfYear(now);
    } else if (comparisonBy === "lastMonth") {
      curStart = startOfMonth(now);
      curEnd = endOfMonth(now);
    } else if (comparisonBy === "lastWeek") {
      curStart = startOfWeek(now);
      curEnd = endOfWeek(now);
    } else if (comparisonBy === "lastQuarter") {
      // For quarters, use current quarter vs previous quarter
      curStart = startOfQuarter(now);
      curEnd = endOfQuarter(now);
    }

    const currentFilters = fromJS({
      ...jsFilters,
      start: {
        operator: "dateRange",
        values: [toDateTime(curStart, false), toDateTime(curEnd, true)],
      },
    });

    const { prevStart, prevEnd } = calculatePreviousPeriod(curStart, curEnd, comparisonBy);
    const previousFilters = fromJS({
      ...jsFilters,
      start: {
        operator: "dateRange",
        values: [toDateTime(prevStart, false), toDateTime(prevEnd, true)],
      },
    });

    return { currentFilters, previousFilters, hasComparison: true };
  }

  let currentFilters = fromJS({
    ...jsFilters,
    start: {
      operator: "dateRange",
      values: [jsFilters.start.values[0], jsFilters.start.values[1]],
    },
  });

  const [curStartStr, curEndStr] = currentFilters.getIn(["start", "values"]).toJS();
  const curStart = toSafeDate(curStartStr);
  const curEnd = toSafeDate(curEndStr);
  const { prevStart, prevEnd } = calculatePreviousPeriod(curStart, curEnd, comparisonBy);

  let previousFilters = fromJS({
    ...jsFilters,
    start: {
      operator: "dateRange",
      values: [toDateTime(prevStart, false), toDateTime(prevEnd, true)],
    },
  });

  return { currentFilters, previousFilters, hasComparison: true };
};

export const buildComparisonUnion = (reportData: ReportData) => {
  const comparisonBy: string = reportData.get("comparisonBy") || "none";
  const baseFilters = reportData.get("filters") || fromJS({});
  const jsFilters =
    typeof (baseFilters as any)?.toJS === "function" ? baseFilters.toJS() : baseFilters;
  const dateUnit: string = reportData.get("dateUnit") || "day";

  // Reuse logic from buildComparisonFilters to compute boundaries
  const hasExplicitRange =
    jsFilters &&
    jsFilters.start &&
    jsFilters.start.operator === "dateRange" &&
    Array.isArray(jsFilters.start.values) &&
    jsFilters.start.values.length >= 2;

  let curStart: Date;
  let curEnd: Date;
  if (!hasExplicitRange) {
    const now = new Date();
    curStart = startOfDay(now);
    curEnd = endOfDay(now);
    if (dateUnit === "week") {
      // using 7-day window anchored at today; here we keep simple startOfDay now
    } else if (dateUnit === "month") {
      curStart = startOfMonth(now);
      curEnd = endOfMonth(now);
    } else if (dateUnit === "year") {
      curStart = startOfYear(now);
      curEnd = endOfYear(now);
    }
  } else {
    const [curStartStr, curEndStr] = jsFilters.start.values;
    curStart = toSafeDate(curStartStr);
    curEnd = toSafeDate(curEndStr);
  }

  if (comparisonBy === "none") {
    const unionFilters = fromJS({
      ...jsFilters,
      start: {
        operator: "dateRange",
        values: [toDateTime(curStart, false), toDateTime(curEnd, true)],
      },
    });
    return { unionFilters, curStart, curEnd, hasComparison: false };
  }

  const { prevStart, prevEnd } = calculatePreviousPeriod(curStart, curEnd, comparisonBy);
  const minStart = prevStart < curStart ? prevStart : curStart;
  const maxEnd = prevEnd > curEnd ? prevEnd : curEnd;
  const unionFilters = fromJS({
    ...jsFilters,
    start: {
      operator: "dateRange",
      values: [toDateTime(minStart, false), toDateTime(maxEnd, true)],
    },
  });

  return { unionFilters, curStart, curEnd, hasComparison: true };
};
